import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import SearchableSelect from '../components/SearchableSelect';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/date';

const PROJECT_STATUSES = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];
// Type column is now a sub-classification (kept for back-compat). The primary
// taxonomy is project_kind below: Internal / External-Dedicated / External-Shared.
const PROJECT_TYPES = ['Implementation', 'AMS', 'Staff Augmentation', 'Advisory', 'Migration', 'Upgrade'];
const PROJECT_KINDS = ['Internal', 'External-Dedicated', 'External-Shared'];
// Pseudo-id for the "+ New Project" form before the row exists. Mirrors the
// pattern used in Opportunities.jsx so the master-detail pane has a single
// rendering path for both new and existing rows.
const NEW_SENTINEL = '_new';
const PHASE_STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'];
const WBS_STATUSES = ['pending', 'in_progress', 'completed', 'on_hold', 'cancelled'];
const ACTIVITY_STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'];
const MILESTONE_SIGNOFF = ['pending', 'signed', 'rejected'];
const DELIVERABLE_STATUSES = ['pending', 'in_progress', 'delivered', 'accepted', 'rejected'];
const RAG = ['green', 'amber', 'red'];

const STATUS_BADGES = {
  planning: 'badge-gray', active: 'badge-green', on_hold: 'badge-yellow',
  completed: 'badge-blue', cancelled: 'badge-red', pending: 'badge-gray',
  in_progress: 'badge-yellow', delivered: 'badge-blue', accepted: 'badge-green',
  rejected: 'badge-red', signed: 'badge-green'
};

const RAG_COLORS = { green: '#10B981', amber: '#F59E0B', red: '#EF4444' };

const EMPTY_PROJECT = {
  name: '', project_code: '', contract_id: '', sow_id: '', type: '', status: 'planning',
  start_date: '', end_date: '', pm_id: '', delivery_head_id: '',
  // ── Kind-aware fields ──
  project_kind: 'External-Dedicated', description: '',
  customer_id: '', cost_center_id: '', department_id: '',
  // For External-Shared: arrays of UUIDs the form has selected.
  customer_ids: [], contract_ids: [],
};

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  // Master lists for kind-aware dropdowns. Kept in state so we only fetch once.
  const [customers, setCustomers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PROJECT });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterKind, setFilterKind] = useState('');
  // Free-text search applied client-side over name/code/customer/contract/PM.
  const [searchText, setSearchText] = useState('');
  // Master-detail in-place edit. editingMode = true makes the detail pane
  // render the project form (instead of the read-only view + tabs).
  const [editingMode, setEditingMode] = useState(false);

  // Detail view
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [progress, setProgress] = useState(null);

  // Sub-modals
  const [subModal, setSubModal] = useState(null);
  const [subForm, setSubForm] = useState({});

  // Unsaved-changes tracking
  const { dirty, markDirty, resetDirty, confirmIfDirty } = useFormDirty();
  const { dirty: subDirty, markDirty: markSubDirty, resetDirty: resetSubDirty } = useFormDirty();

  const fetchProjects = useCallback(async () => {
    try {
      let url = '/api/projects?';
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterType) url += `type=${filterType}&`;
      // Kind filter is applied client-side because the existing /api/projects
      // route only knows about status + type + pmId — keeps backend untouched.
      const [pRes, uRes, cRes, ctRes, dRes] = await Promise.all([
        apiFetch(url),
        apiFetch('/api/admin/users?limit=200'),
        apiFetch('/api/customers?limit=500'),
        apiFetch('/api/contracts?limit=500'),
        apiFetch('/api/admin/departments').catch(() => ({ ok: false })),
      ]);
      if (pRes.ok)  { const d = await pRes.json();  setProjects(d.projects || []); }
      if (uRes.ok)  { const d = await uRes.json();  setUsers(d.users || d.data || []); }
      if (cRes.ok)  { const d = await cRes.json();  setCustomers(d.customers || d.data || []); }
      if (ctRes.ok) { const d = await ctRes.json(); setContracts(d.contracts || d.data || []); }
      if (dRes.ok)  { const d = await dRes.json();  setDepartments(d.departments || d.data || []); }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, [filterStatus, filterType]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const fmtDate = (v) => formatDate(v, '--');

  async function fetchDetail(id) {
    try {
      const [dRes, pRes] = await Promise.all([
        apiFetch(`/api/projects/${id}`),
        apiFetch(`/api/projects/${id}/progress`)
      ]);
      if (dRes.ok) { const d = await dRes.json(); setDetail(d); }
      if (pRes.ok) { const d = await pRes.json(); setProgress(d); }
    } catch (e) { /* handled */ }
  }

  // Canonical FormField onChange handler — FormField calls onChange(name, value)
  // with TWO arguments. Single-arg `v => ...` handlers silently drop the value
  // because v ends up being the field name, not the typed text. Always pass
  // `name="..." onChange={handleField}` on FormFields rather than inlining a
  // setForm call. Mirrors the pattern used in Employees.jsx.
  function handleField(name, value) {
    setForm(f => ({ ...f, [name]: value }));
    markDirty();
  }

  // ── New / Edit flows happen INLINE in the detail pane, not in a modal.
  //    selected.id === NEW_SENTINEL means "blank form for create" — the detail
  //    pane renders its edit form when isNew or editingMode is true.
  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_PROJECT });
    setErrors({});
    resetDirty();
    setSelected({ id: NEW_SENTINEL, name: '(new project)' });
    setDetail({ project: { ...EMPTY_PROJECT } });
    setEditingMode(true);
    setDetailTab('overview');
  }
  async function openEdit(p) {
    setEditing(p);
    // For External-Shared, hydrate the join arrays from the detail endpoint so
    // the multi-select shows what's already linked. For other kinds we leave
    // the arrays empty — the form rows that use them aren't rendered anyway.
    let customer_ids = []; let contract_ids = [];
    if (p.project_kind === 'External-Shared') {
      try {
        const dRes = await apiFetch(`/api/projects/${p.id}`);
        if (dRes.ok) {
          const d = await dRes.json();
          customer_ids = (d.sharedCustomers || []).map(r => r.customer_id);
          contract_ids = (d.sharedContracts || []).map(r => r.contract_id);
        }
      } catch (e) { /* fall through with empty arrays */ }
    }
    setForm({
      name: p.name || '', project_code: p.project_code || '', contract_id: p.contract_id || '',
      sow_id: p.sow_id || '', type: p.type || '', status: p.status || 'planning',
      start_date: p.start_date ? p.start_date.substring(0, 10) : '',
      end_date: p.end_date ? p.end_date.substring(0, 10) : '',
      pm_id: p.pm_id || '', delivery_head_id: p.delivery_head_id || '',
      project_kind: p.project_kind || 'External-Dedicated',
      description: p.description || '',
      customer_id: p.customer_id || '',
      cost_center_id: p.cost_center_id || '',
      department_id: p.department_id || '',
      customer_ids, contract_ids,
    });
    setErrors({}); resetDirty();
    setSelected(p);
    fetchDetail(p.id);
    setEditingMode(true);
    setDetailTab('overview');
  }
  function cancelEdit() {
    if (!confirmIfDirty()) return;
    if (selected && selected.id === NEW_SENTINEL) {
      setSelected(null); setDetail(null);
    }
    setEditingMode(false);
    setEditing(null);
    resetDirty();
  }

  async function handleSave() {
    if (!form.name.trim()) { setErrors({ name: 'Project name is required' }); return; }
    setSaving(true);
    // Strip kind-irrelevant FKs so we don't write a customer_id onto an Internal
    // project just because the user toggled kinds while filling the form.
    const kind = form.project_kind || 'External-Dedicated';
    const body = {
      name: form.name, projectCode: form.project_code || null,
      contractId: kind === 'External-Dedicated' ? (form.contract_id || null) : null,
      sowId: form.sow_id || null, type: form.type || null, status: form.status,
      startDate: form.start_date || null, endDate: form.end_date || null,
      pmId: form.pm_id || null, deliveryHeadId: form.delivery_head_id || null,
      description: form.description || null,
      projectKind: kind,
      customerId: kind === 'External-Dedicated' ? (form.customer_id || null) : null,
      costCenterId: kind === 'Internal' ? (form.cost_center_id || null) : null,
      departmentId:  kind === 'Internal' ? (form.department_id  || null) : null,
      customerIds: kind === 'External-Shared' ? (form.customer_ids || []) : [],
      contractIds: kind === 'External-Shared' ? (form.contract_ids || []) : [],
    };
    // editing === null means "we're saving a brand-new project (POST)";
    // otherwise we're updating an existing one (PUT). Now that there's no
    // modal, the post-save behaviour is to drop out of edit mode and re-fetch
    // the detail so the read-only view reflects what was just saved.
    const isUpdate = !!editing;
    const url = isUpdate ? `/api/projects/${editing.id}` : '/api/projects';
    const method = isUpdate ? 'PUT' : 'POST';
    try {
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        resetDirty(); setEditingMode(false); setEditing(null);
        await fetchProjects();
        if (isUpdate) {
          // Re-fetch the saved detail so the read-only view shows fresh data.
          if (selected && selected.id) fetchDetail(selected.id);
        } else if (data && data.id) {
          // Brand-new row — select it so the detail pane snaps to it.
          selectProject({ id: data.id, name: data.name || form.name });
        }
      } else { const d = await res.json(); setErrors({ name: d.error }); }
    } catch (e) { setErrors({ name: 'Failed to save' }); }
    setSaving(false);
  }

  async function handleDelete(p) {
    if (!confirm(`Delete project "${p.name}"?`)) return;
    try {
      await apiFetch(`/api/projects/${p.id}`, { method: 'DELETE' });
      fetchProjects();
    } catch (e) { /* handled */ }
  }

  function selectProject(p) {
    setSelected(p);
    setDetailTab('overview');
    setEditingMode(false);     // clicking a row drops out of edit mode
    setEditing(null);
    fetchDetail(p.id);
  }

  // ---- Sub-entity saves ----
  async function savePhase() {
    const body = {
      name: subForm.name, sequence: parseInt(subForm.sequence) || 0,
      plannedStart: subForm.planned_start || null, plannedEnd: subForm.planned_end || null,
      status: subForm.status || 'pending'
    };
    const url = subForm.id
      ? `/api/projects/${selected.id}/phases/${subForm.id}`
      : `/api/projects/${selected.id}/phases`;
    await apiFetch(url, { method: subForm.id ? 'PUT' : 'POST', body: JSON.stringify(body) });
    resetSubDirty(); setSubModal(null); fetchDetail(selected.id);
  }

  async function saveMilestone() {
    const body = {
      name: subForm.name, phaseId: subForm.phase_id || null,
      plannedDate: subForm.planned_date || null, actualDate: subForm.actual_date || null,
      signoffStatus: subForm.signoff_status || 'pending',
      billingTrigger: subForm.billing_trigger || false,
      amount: subForm.amount || null
    };
    const url = subForm.id
      ? `/api/projects/${selected.id}/milestones/${subForm.id}`
      : `/api/projects/${selected.id}/milestones`;
    await apiFetch(url, { method: subForm.id ? 'PUT' : 'POST', body: JSON.stringify(body) });
    resetSubDirty(); setSubModal(null); fetchDetail(selected.id);
  }

  async function saveTeamMember() {
    const body = {
      employeeId: subForm.employee_id, role: subForm.role || null,
      startDate: subForm.start_date || null, endDate: subForm.end_date || null
    };
    const url = subForm.id
      ? `/api/projects/${selected.id}/team/${subForm.id}`
      : `/api/projects/${selected.id}/team`;
    await apiFetch(url, { method: subForm.id ? 'PUT' : 'POST', body: JSON.stringify(body) });
    resetSubDirty(); setSubModal(null); fetchDetail(selected.id);
  }

  async function saveDeliverable() {
    const body = {
      name: subForm.name, description: subForm.description || null,
      dueDate: subForm.due_date || null, deliveredDate: subForm.delivered_date || null,
      status: subForm.status || 'pending'
    };
    const url = subForm.id
      ? `/api/projects/${selected.id}/deliverables/${subForm.id}`
      : `/api/projects/${selected.id}/deliverables`;
    await apiFetch(url, { method: subForm.id ? 'PUT' : 'POST', body: JSON.stringify(body) });
    resetSubDirty(); setSubModal(null); fetchDetail(selected.id);
  }

  // ---- Detail Pane (right side of master-detail) ----
  // Built as an expression rather than an early-return so the master pane
  // remains visible alongside it. The legacy "Back to Projects" button is
  // gone — selection is communicated by which row is highlighted in the
  // master list. Closing the detail = clicking elsewhere or selecting another.
  const detailPane = (selected && detail) ? (() => {
    const p = detail.project;
    const pct = progress ? progress.percentComplete : 0;
    const isNew = selected.id === NEW_SENTINEL;
    const showForm = isNew || editingMode;
    return (
      <div key={selected.id}>
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <h2 style={{ margin: 0 }}>{showForm ? (form.name || (isNew ? 'New project' : 'Untitled')) : p.name}</h2>
              <span style={{ color: '#64748B' }}>{(showForm ? form.project_code : p.project_code) || 'No code'} | {(showForm ? form.type : p.type) || 'N/A'}</span>
            </div>
            {/* Action buttons — Save/Cancel when editing or new; Edit/Delete on existing read-only. */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {!showForm && (
                <span className={`badge ${STATUS_BADGES[p.status] || 'badge-gray'}`}>{p.status}</span>
              )}
              {!showForm && !isNew && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p)}>Delete</button>
                </>
              )}
              {showForm && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}
                  </button>
                </>
              )}
            </div>
          </div>
          {!showForm && (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 14 }}>
            <div><strong>PM:</strong> {p.pm_name || '--'}</div>
            <div><strong>Start:</strong> {fmtDate(p.start_date)}</div>
            <div><strong>End:</strong> {fmtDate(p.end_date)}</div>
          </div>
          )}
          {!showForm && !isNew && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <strong>Progress:</strong>
              <div style={{ flex: 1, background: '#E2E8F0', borderRadius: 8, height: 20, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, background: '#6366F1', height: '100%', borderRadius: 8, transition: 'width 0.3s' }} />
              </div>
              <span>{pct}%</span>
            </div>
          </div>
          )}
        </div>

        {/* ── Inline edit form. Renders when creating a new project or after
              clicking Edit on an existing one. The fields use the canonical
              `name="..."` prop + `handleField` 2-arg handler — single-arg
              `v=>...` handlers silently drop input because FormField calls
              onChange(name, value). */}
        {showForm && (
          <div className="card" style={{ marginBottom: 16 }}>
            <FormField label="Name" name="name" value={form.name} onChange={handleField} error={errors.name} required />
            <FormField label="Description" type="textarea" rows={2} name="description"
              value={form.description} onChange={handleField} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Project Code" name="project_code" value={form.project_code} onChange={handleField} />
              <FormField label="Project Kind" name="project_kind" type="select" required
                options={PROJECT_KINDS.map(k => ({ value: k, label: k }))}
                value={form.project_kind} onChange={handleField} />
            </div>
            {form.project_kind === 'Internal' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Department" name="department_id" type="select"
                  options={departments.map(d => ({ value: d.id, label: d.name }))}
                  value={form.department_id} onChange={handleField} />
                <FormField label="Cost Center" name="cost_center_id" value={form.cost_center_id}
                  onChange={handleField} placeholder="Cost center code (optional)" />
              </div>
            )}
            {form.project_kind === 'External-Dedicated' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Customer" name="customer_id" type="select"
                  options={customers.map(c => ({ value: c.id, label: c.name }))}
                  value={form.customer_id} onChange={handleField} />
                <FormField label="Contract" name="contract_id" type="select"
                  options={contracts
                    .filter(c => !form.customer_id || c.customer_id === form.customer_id)
                    .map(c => ({ value: c.id, label: c.contract_number || c.title || c.id.slice(0,8) }))}
                  value={form.contract_id} onChange={handleField} />
              </div>
            )}
            {form.project_kind === 'External-Shared' && (
              <>
                <MultiPicker label="Customers"
                  options={customers.map(c => ({ value: c.id, label: c.name }))}
                  values={form.customer_ids}
                  onChange={(arr) => { setForm(f => ({ ...f, customer_ids: arr })); markDirty(); }}
                  placeholder="Add customer..." />
                <MultiPicker label="Contracts"
                  options={contracts.map(c => ({ value: c.id, label: c.contract_number || c.title || c.id.slice(0,8) }))}
                  values={form.contract_ids}
                  onChange={(arr) => { setForm(f => ({ ...f, contract_ids: arr })); markDirty(); }}
                  placeholder="Add contract..." />
              </>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Type" name="type" type="select"
                options={PROJECT_TYPES.map(t => ({ value: t, label: t }))}
                value={form.type} onChange={handleField} />
              <FormField label="Status" name="status" type="select"
                options={PROJECT_STATUSES.map(s => ({ value: s, label: s }))}
                value={form.status} onChange={handleField} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Start Date" name="start_date" type="date" value={form.start_date} onChange={handleField} />
              <FormField label="End Date" name="end_date" type="date" value={form.end_date} onChange={handleField} />
            </div>
            <FormField label="PM" name="pm_id" type="employee-search"
              options={users.map(u => ({ value: u.id, label: u.full_name })).sort((a, b) => a.label.localeCompare(b.label))}
              value={form.pm_id} onChange={handleField} placeholder="Search PM..." />
            <FormField label="Delivery Head" name="delivery_head_id" type="employee-search"
              options={users.map(u => ({ value: u.id, label: u.full_name })).sort((a, b) => a.label.localeCompare(b.label))}
              value={form.delivery_head_id} onChange={handleField} placeholder="Search Delivery Head..." />
          </div>
        )}

        {/* Tabs — only shown for existing rows in read-only mode. New project
            stays focused on the form until it's saved. */}
        {!showForm && !isNew && (
        <div className="tab-bar" style={{ marginBottom: 16 }}>
          {['overview', 'wbs', 'network', 'phases', 'milestones', 'team', 'deliverables', 'documents'].map(t => (
            <button key={t} className={`tab-btn${detailTab === t ? ' active' : ''}`} onClick={() => setDetailTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        )}

        {/* WBS Tab — tree of breakdown nodes, with Seed-from-Contract action
            and the ability to attach billing milestones from any contract
            associated with this project. */}
        {!showForm && detailTab === 'wbs' && (
          <WbsTab project={detail.project}
            wbs={detail.wbs || []}
            billingMilestones={detail.billingMilestones || []}
            sharedContracts={detail.sharedContracts || []}
            users={users}
            onChange={() => fetchDetail(selected.id)} />
        )}

        {/* Network Tab — flat activity list with predecessors. Each activity
            hangs under a WBS node so the network and breakdown stay aligned. */}
        {!showForm && detailTab === 'network' && (
          <NetworkTab project={detail.project}
            activities={detail.activities || []}
            wbs={detail.wbs || []}
            users={users}
            onChange={() => fetchDetail(selected.id)} />
        )}

        {/* Phases Tab */}
        {!showForm && detailTab === 'phases' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3>Phases</h3>
              <button className="btn btn-primary btn-sm" onClick={() => { setSubForm({ name: '', sequence: '', planned_start: '', planned_end: '', status: 'pending' }); resetSubDirty(); setSubModal('phase'); }}>
                + Add Phase
              </button>
            </div>
            <table className="data-table">
              <thead><tr><th>#</th><th>Name</th><th>Planned Start</th><th>Planned End</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {(detail.phases || []).map(ph => (
                  <tr key={ph.id}>
                    <td>{ph.sequence}</td>
                    <td>{ph.name}</td>
                    <td>{fmtDate(ph.planned_start)}</td>
                    <td>{fmtDate(ph.planned_end)}</td>
                    <td><span className={`badge ${STATUS_BADGES[ph.status] || 'badge-gray'}`}>{ph.status}</span></td>
                    <td>
                      <button className="btn btn-secondary btn-xs" onClick={() => { setSubForm({ ...ph, planned_start: ph.planned_start ? ph.planned_start.substring(0,10) : '', planned_end: ph.planned_end ? ph.planned_end.substring(0,10) : '' }); resetSubDirty(); setSubModal('phase'); }}>Edit</button>
                    </td>
                  </tr>
                ))}
                {(!detail.phases || detail.phases.length === 0) && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94A3B8' }}>No phases yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Milestones Tab */}
        {!showForm && detailTab === 'milestones' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3>Milestones</h3>
              <button className="btn btn-primary btn-sm" onClick={() => { setSubForm({ name: '', planned_date: '', signoff_status: 'pending', billing_trigger: false, amount: '' }); resetSubDirty(); setSubModal('milestone'); }}>
                + Add Milestone
              </button>
            </div>
            <table className="data-table">
              <thead><tr><th>Name</th><th>Planned</th><th>Actual</th><th>Signoff</th><th>Billing</th><th>Amount</th><th>Actions</th></tr></thead>
              <tbody>
                {(detail.milestones || []).map(ms => (
                  <tr key={ms.id}>
                    <td>{ms.name}</td>
                    <td>{fmtDate(ms.planned_date)}</td>
                    <td>{fmtDate(ms.actual_date)}</td>
                    <td><span className={`badge ${STATUS_BADGES[ms.signoff_status] || 'badge-gray'}`}>{ms.signoff_status}</span></td>
                    <td>{ms.billing_trigger ? 'Yes' : 'No'}</td>
                    <td>{ms.amount ? formatCurrency(ms.amount) : '--'}</td>
                    <td>
                      <button className="btn btn-secondary btn-xs" onClick={() => { setSubForm({ ...ms, planned_date: ms.planned_date ? ms.planned_date.substring(0,10) : '', actual_date: ms.actual_date ? ms.actual_date.substring(0,10) : '' }); resetSubDirty(); setSubModal('milestone'); }}>Edit</button>
                    </td>
                  </tr>
                ))}
                {(!detail.milestones || detail.milestones.length === 0) && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94A3B8' }}>No milestones yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Team Tab */}
        {!showForm && detailTab === 'team' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3>Team</h3>
              <button className="btn btn-primary btn-sm" onClick={() => { setSubForm({ employee_id: '', role: '', start_date: '', end_date: '' }); resetSubDirty(); setSubModal('team'); }}>
                + Add Member
              </button>
            </div>
            <table className="data-table">
              <thead><tr><th>Employee</th><th>Role</th><th>Start</th><th>End</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {(detail.team || []).map(tm => (
                  <tr key={tm.id}>
                    <td>{tm.first_name ? `${tm.first_name} ${tm.last_name}` : tm.employee_id}</td>
                    <td>{tm.role || '--'}</td>
                    <td>{fmtDate(tm.start_date)}</td>
                    <td>{fmtDate(tm.end_date)}</td>
                    <td>{tm.active ? 'Yes' : 'No'}</td>
                    <td>
                      <button className="btn btn-secondary btn-xs" onClick={() => { setSubForm({ ...tm, start_date: tm.start_date ? tm.start_date.substring(0,10) : '', end_date: tm.end_date ? tm.end_date.substring(0,10) : '' }); resetSubDirty(); setSubModal('team'); }}>Edit</button>
                    </td>
                  </tr>
                ))}
                {(!detail.team || detail.team.length === 0) && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94A3B8' }}>No team members yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Deliverables Tab */}
        {!showForm && detailTab === 'deliverables' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3>Deliverables</h3>
              <button className="btn btn-primary btn-sm" onClick={() => { setSubForm({ name: '', description: '', due_date: '', status: 'pending' }); resetSubDirty(); setSubModal('deliverable'); }}>
                + Add Deliverable
              </button>
            </div>
            <table className="data-table">
              <thead><tr><th>Name</th><th>Due</th><th>Delivered</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {(detail.deliverables || []).map(d => (
                  <tr key={d.id}>
                    <td>{d.name}</td>
                    <td>{fmtDate(d.due_date)}</td>
                    <td>{fmtDate(d.delivered_date)}</td>
                    <td><span className={`badge ${STATUS_BADGES[d.status] || 'badge-gray'}`}>{d.status}</span></td>
                    <td>
                      <button className="btn btn-secondary btn-xs" onClick={() => { setSubForm({ ...d, due_date: d.due_date ? d.due_date.substring(0,10) : '', delivered_date: d.delivered_date ? d.delivered_date.substring(0,10) : '' }); resetSubDirty(); setSubModal('deliverable'); }}>Edit</button>
                    </td>
                  </tr>
                ))}
                {(!detail.deliverables || detail.deliverables.length === 0) && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94A3B8' }}>No deliverables yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Documents Tab */}
        {!showForm && detailTab === 'documents' && (
          <div className="card">
            <h3>Documents</h3>
            <table className="data-table">
              <thead><tr><th>Name</th><th>Type</th><th>Uploaded</th></tr></thead>
              <tbody>
                {(detail.documents || []).map(doc => (
                  <tr key={doc.id}>
                    <td><a href={doc.file_url} target="_blank" rel="noreferrer">{doc.name}</a></td>
                    <td>{doc.type || '--'}</td>
                    <td>{fmtDate(doc.uploaded_at)}</td>
                  </tr>
                ))}
                {(!detail.documents || detail.documents.length === 0) && <tr><td colSpan={3} style={{ textAlign: 'center', color: '#94A3B8' }}>No documents yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Overview Tab */}
        {!showForm && detailTab === 'overview' && (
          <div className="card">
            <h3>Project Overview</h3>
            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
              <div><strong>Delivery Head:</strong> {p.delivery_head_name || '--'}</div>
              <div><strong>Contract:</strong> {p.contract_number || '--'}</div>
              <div><strong>SOW ID:</strong> {p.sow_id || '--'}</div>
              <div><strong>Created:</strong> {fmtDate(p.created_at)}</div>
            </div>
          </div>
        )}

        {/* Sub Modals */}
        {subModal === 'phase' && (
          <Modal isOpen title={subForm.id ? 'Edit Phase' : 'Add Phase'} onClose={() => setSubModal(null)} dirty={subDirty}>
            <FormField label="Name" value={subForm.name} onChange={v => { setSubForm(f => ({ ...f, name: v })); markSubDirty(); }} required />
            <FormField label="Sequence" type="number" value={subForm.sequence} onChange={v => { setSubForm(f => ({ ...f, sequence: v })); markSubDirty(); }} />
            <FormField label="Planned Start" type="date" value={subForm.planned_start} onChange={v => { setSubForm(f => ({ ...f, planned_start: v })); markSubDirty(); }} />
            <FormField label="Planned End" type="date" value={subForm.planned_end} onChange={v => { setSubForm(f => ({ ...f, planned_end: v })); markSubDirty(); }} />
            <FormField label="Status" type="select" options={PHASE_STATUSES.map(s => ({ value: s, label: s }))} value={subForm.status} onChange={v => { setSubForm(f => ({ ...f, status: v })); markSubDirty(); }} />
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSubModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePhase}>Save</button>
            </div>
          </Modal>
        )}
        {subModal === 'milestone' && (
          <Modal isOpen title={subForm.id ? 'Edit Milestone' : 'Add Milestone'} onClose={() => setSubModal(null)} dirty={subDirty}>
            <FormField label="Name" value={subForm.name} onChange={v => { setSubForm(f => ({ ...f, name: v })); markSubDirty(); }} required />
            <FormField label="Planned Date" type="date" value={subForm.planned_date} onChange={v => { setSubForm(f => ({ ...f, planned_date: v })); markSubDirty(); }} />
            <FormField label="Actual Date" type="date" value={subForm.actual_date} onChange={v => { setSubForm(f => ({ ...f, actual_date: v })); markSubDirty(); }} />
            <FormField label="Signoff Status" type="select" options={MILESTONE_SIGNOFF.map(s => ({ value: s, label: s }))} value={subForm.signoff_status} onChange={v => { setSubForm(f => ({ ...f, signoff_status: v })); markSubDirty(); }} />
            <FormField label="Amount" type="number" value={subForm.amount} onChange={v => { setSubForm(f => ({ ...f, amount: v })); markSubDirty(); }} />
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSubModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveMilestone}>Save</button>
            </div>
          </Modal>
        )}
        {subModal === 'team' && (
          <Modal isOpen title={subForm.id ? 'Edit Team Member' : 'Add Team Member'} onClose={() => setSubModal(null)} dirty={subDirty}>
            <FormField label="Employee ID" value={subForm.employee_id} onChange={v => { setSubForm(f => ({ ...f, employee_id: v })); markSubDirty(); }} required />
            <FormField label="Role" value={subForm.role} onChange={v => { setSubForm(f => ({ ...f, role: v })); markSubDirty(); }} />
            <FormField label="Start Date" type="date" value={subForm.start_date} onChange={v => { setSubForm(f => ({ ...f, start_date: v })); markSubDirty(); }} />
            <FormField label="End Date" type="date" value={subForm.end_date} onChange={v => { setSubForm(f => ({ ...f, end_date: v })); markSubDirty(); }} />
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSubModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveTeamMember}>Save</button>
            </div>
          </Modal>
        )}
        {subModal === 'deliverable' && (
          <Modal isOpen title={subForm.id ? 'Edit Deliverable' : 'Add Deliverable'} onClose={() => setSubModal(null)} dirty={subDirty}>
            <FormField label="Name" value={subForm.name} onChange={v => { setSubForm(f => ({ ...f, name: v })); markSubDirty(); }} required />
            <FormField label="Description" type="textarea" value={subForm.description} onChange={v => { setSubForm(f => ({ ...f, description: v })); markSubDirty(); }} />
            <FormField label="Due Date" type="date" value={subForm.due_date} onChange={v => { setSubForm(f => ({ ...f, due_date: v })); markSubDirty(); }} />
            <FormField label="Status" type="select" options={DELIVERABLE_STATUSES.map(s => ({ value: s, label: s }))} value={subForm.status} onChange={v => { setSubForm(f => ({ ...f, status: v })); markSubDirty(); }} />
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSubModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveDeliverable}>Save</button>
            </div>
          </Modal>
        )}
      </div>
    );
  })() : null;

  // ── Filtered list for the master pane. Status + Type live in the URL query
  //    (handled by fetchProjects). Kind filtering and the free-text search
  //    are applied client-side here so changing them doesn't refetch.
  const filteredProjects = projects.filter(p => {
    if (filterKind && p.project_kind !== filterKind) return false;
    if (!searchText) return true;
    const blob = [p.name, p.project_code, p.customer_name, p.contract_number, p.pm_name, p.type, p.project_kind]
      .filter(Boolean).join(' ').toLowerCase();
    return blob.includes(searchText.toLowerCase());
  });

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', background: '#0B1020' }}>
      {/* ─────────── Master pane ─────────── */}
      <aside style={{ width: 360, flexShrink: 0, borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 14, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
            <span style={{ fontSize: 12, color: '#64748B' }}>{filteredProjects.length} of {projects.length}</span>
            <button onClick={openCreate} className="btn btn-primary btn-sm">+ New Project</button>
          </div>
          <input
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search projects…"
            className="form-control"
            style={{ marginBottom: 8 }}
          />
          {/* Modern, search-enabled filters. Each one slots into the master
              pane vertically since horizontal space is tight at 360px. */}
          <div style={{ marginBottom: 8 }}>
            <SearchableSelect
              value={filterStatus}
              onChange={setFilterStatus}
              options={[{ value: '', label: 'All Statuses' }, ...PROJECT_STATUSES.map(s => ({ value: s, label: s }))]}
              placeholder="All Statuses"
              sort={false}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <SearchableSelect
              value={filterKind}
              onChange={setFilterKind}
              options={[{ value: '', label: 'All Kinds' }, ...PROJECT_KINDS.map(k => ({ value: k, label: k }))]}
              placeholder="All Kinds"
              sort={false}
            />
          </div>
          <div>
            <SearchableSelect
              value={filterType}
              onChange={setFilterType}
              options={[{ value: '', label: 'All Types' }, ...PROJECT_TYPES.map(t => ({ value: t, label: t }))]}
              placeholder="All Types"
              sort={false}
            />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <div style={{ padding: 16, color: '#94A3B8' }}>Loading…</div>}
          {!loading && filteredProjects.length === 0 && (
            <div style={{ padding: 16, color: '#64748B', fontSize: 12.5 }}>No projects match.</div>
          )}
          {filteredProjects.map(p => (
            <ProjectListRow
              key={p.id}
              project={p}
              selected={selected && selected.id === p.id}
              onClick={() => selectProject(p)}
              onEdit={() => openEdit(p)}
              onDelete={() => handleDelete(p)}
            />
          ))}
        </div>
      </aside>

      {/* ─────────── Detail pane ─────────── */}
      <main style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {!selected && (
          <div style={{ padding: 60, textAlign: 'center', color: '#64748B' }}>
            Select a project from the list, or click <strong>+ New Project</strong>.
          </div>
        )}
        {selected && !detail && (
          <div style={{ padding: 60, textAlign: 'center', color: '#94A3B8' }}>Loading project…</div>
        )}
        {detailPane}
      </main>

      {/* Modal-based create/edit was removed in favour of inline editing in
          the detail pane. The form fields above use the canonical
          name+handleField pattern so input actually persists. */}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * MultiPicker — chip-style multi-value picker on top of SearchableSelect.
 * Used by the External-Shared project form rows (multi-customer / multi-contract).
 * Values are kept as an array of IDs in the parent's form state; the inner
 * <SearchableSelect> is single-select and we add picked items into the chip
 * row above it. Clicking a chip removes it.
 * ════════════════════════════════════════════════════════════════════════ */
function MultiPicker({ label, options, values, onChange, placeholder }) {
  const selected = values || [];
  const remaining = options.filter(o => !selected.includes(o.value));
  const labelFor = (id) => (options.find(o => o.value === id)?.label) || id;
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {selected.map(id => (
          <span key={id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(0,224,255,0.10)', border: '1px solid rgba(0,224,255,0.40)',
            fontSize: 12, color: '#E5E7EB',
          }}>
            {labelFor(id)}
            <button type="button" onClick={() => onChange(selected.filter(s => s !== id))}
              style={{ background: 'none', border: 'none', color: '#FB7185', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
          </span>
        ))}
        {selected.length === 0 && <span style={{ color: '#94A3B8', fontSize: 12 }}>None selected</span>}
      </div>
      <SearchableSelect
        value=""
        onChange={(v) => { if (v) onChange([...selected, v]); }}
        options={remaining}
        placeholder={placeholder || `Add ${label}...`}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * WbsTab — tree view of project_wbs rows. Supports:
 *   - Seeding from a contract (auto-mirror of Line Items + Sub-Items)
 *   - Add/edit/delete WBS nodes
 *   - Attach/detach billing milestones to a node
 * Tree is rendered from the flat array via parent_id grouping.
 * ════════════════════════════════════════════════════════════════════════ */
function WbsTab({ project, wbs, billingMilestones, sharedContracts, users, onChange }) {
  const [editing, setEditing] = React.useState(null);
  const [seedContractId, setSeedContractId] = React.useState('');
  const [attachOpen, setAttachOpen] = React.useState(null); // { wbsId }
  const [milestoneToAttach, setMilestoneToAttach] = React.useState('');

  // Eligible contracts = the dedicated contract OR any of the shared contracts
  const contractOptions = React.useMemo(() => {
    const opts = [];
    if (project.contract_id && project.contract_number) {
      opts.push({ value: project.contract_id, label: project.contract_number });
    }
    sharedContracts.forEach(sc => {
      if (!opts.find(o => o.value === sc.contract_id)) {
        opts.push({ value: sc.contract_id, label: sc.contract_number || sc.contract_id.slice(0, 8) });
      }
    });
    return opts;
  }, [project, sharedContracts]);

  async function seed() {
    if (!seedContractId) return;
    await apiFetch(`/api/projects/${project.id}/wbs/seed-from-contract`, {
      method: 'POST', body: JSON.stringify({ contractId: seedContractId })
    });
    setSeedContractId(''); onChange();
  }

  async function saveNode(node) {
    const url = node.id ? `/api/projects/${project.id}/wbs/${node.id}` : `/api/projects/${project.id}/wbs`;
    const method = node.id ? 'PUT' : 'POST';
    await apiFetch(url, { method, body: JSON.stringify({
      parentId: node.parent_id || null, name: node.name, description: node.description,
      wbsCode: node.wbs_code, plannedStart: node.planned_start || null,
      plannedEnd: node.planned_end || null, status: node.status, ownerId: node.owner_id || null,
      pctComplete: node.pct_complete, weightPct: node.weight_pct,
    }) });
    setEditing(null); onChange();
  }
  async function deleteNode(id) {
    if (!confirm('Delete this WBS node and all its children?')) return;
    await apiFetch(`/api/projects/${project.id}/wbs/${id}`, { method: 'DELETE' });
    onChange();
  }
  async function attach() {
    if (!milestoneToAttach || !attachOpen) return;
    await apiFetch(`/api/projects/${project.id}/wbs/${attachOpen.wbsId}/attach-milestone`, {
      method: 'POST', body: JSON.stringify({ milestoneId: milestoneToAttach }),
    });
    setAttachOpen(null); setMilestoneToAttach(''); onChange();
  }
  async function detach(wbsId, milestoneId) {
    await apiFetch(`/api/projects/${project.id}/wbs/${wbsId}/detach-milestone`, {
      method: 'POST', body: JSON.stringify({ milestoneId }),
    });
    onChange();
  }

  // Build tree
  const byParent = {};
  wbs.forEach(n => { (byParent[n.parent_id || 'root'] = byParent[n.parent_id || 'root'] || []).push(n); });
  const milestonesByWbs = {};
  billingMilestones.forEach(m => { if (m.wbs_id) (milestonesByWbs[m.wbs_id] = milestonesByWbs[m.wbs_id] || []).push(m); });

  function renderTree(nodes, depth) {
    return (nodes || []).map(n => (
      <React.Fragment key={n.id}>
        <tr>
          <td style={{ paddingLeft: 12 + depth * 22, fontFamily: 'monospace', fontSize: 12, color: '#6FE6FF' }}>{n.wbs_code || '—'}</td>
          <td><strong>{n.name}</strong>{n.line_item_id && <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(0,224,255,0.10)', color: '#6FE6FF', padding: '1px 6px', borderRadius: 4 }}>contract</span>}</td>
          <td>{n.planned_start ? n.planned_start.substring(0, 10) : '--'}</td>
          <td>{n.planned_end ? n.planned_end.substring(0, 10) : '--'}</td>
          <td><span className={`badge ${STATUS_BADGES[n.status] || 'badge-gray'}`}>{n.status}</span></td>
          <td>
            {(milestonesByWbs[n.id] || []).map(m => (
              <span key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', marginRight: 4, marginBottom: 2, borderRadius: 999, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)', fontSize: 11 }}>
                {m.description || m.contract_number}
                <button onClick={() => detach(n.id, m.id)} style={{ background: 'none', border: 'none', color: '#FB7185', cursor: 'pointer' }}>×</button>
              </span>
            ))}
            <button className="btn btn-secondary btn-xs" onClick={() => setAttachOpen({ wbsId: n.id })}>+ Attach</button>
          </td>
          <td>
            <button className="btn btn-secondary btn-xs" onClick={() => setEditing({ ...n, _isChild: false })}>Edit</button>
            <button className="btn btn-secondary btn-xs" style={{ marginLeft: 4 }} onClick={() => setEditing({ parent_id: n.id, status: 'pending', _isChild: true })}>+Child</button>
            <button className="btn btn-danger btn-xs" style={{ marginLeft: 4 }} onClick={() => deleteNode(n.id)}>Del</button>
          </td>
        </tr>
        {renderTree(byParent[n.id], depth + 1)}
      </React.Fragment>
    ));
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Work Breakdown Structure</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {contractOptions.length > 0 && (
            <>
              <select className="form-select" value={seedContractId} onChange={e => setSeedContractId(e.target.value)}>
                <option value="">Seed from contract...</option>
                {contractOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button className="btn btn-secondary" disabled={!seedContractId} onClick={seed}>Seed</button>
            </>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => setEditing({ status: 'pending' })}>+ Add Node</button>
        </div>
      </div>
      <table className="data-table">
        <thead><tr><th>Code</th><th>Name</th><th>Start</th><th>End</th><th>Status</th><th>Billing Milestones</th><th>Actions</th></tr></thead>
        <tbody>
          {renderTree(byParent.root, 0)}
          {wbs.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94A3B8', padding: 30 }}>No WBS yet. Seed from a contract or add a node.</td></tr>}
        </tbody>
      </table>

      {editing && (
        <Modal isOpen title={editing.id ? 'Edit WBS Node' : 'New WBS Node'} onClose={() => setEditing(null)}>
          <FormField label="Name" value={editing.name || ''} onChange={v => setEditing({ ...editing, name: v })} required />
          <FormField label="WBS Code" value={editing.wbs_code || ''} onChange={v => setEditing({ ...editing, wbs_code: v })} />
          <FormField label="Description" type="textarea" value={editing.description || ''} onChange={v => setEditing({ ...editing, description: v })} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Planned Start" type="date" value={editing.planned_start ? editing.planned_start.substring(0,10) : ''} onChange={v => setEditing({ ...editing, planned_start: v })} />
            <FormField label="Planned End" type="date" value={editing.planned_end ? editing.planned_end.substring(0,10) : ''} onChange={v => setEditing({ ...editing, planned_end: v })} />
          </div>
          <FormField label="Status" type="select" options={WBS_STATUSES.map(s => ({ value: s, label: s }))} value={editing.status || 'pending'} onChange={v => setEditing({ ...editing, status: v })} />
          <FormField label="Owner" type="employee-search" name="owner_id" options={users.map(u => ({ value: u.id, label: u.full_name }))} value={editing.owner_id || ''} onChange={(n, v) => setEditing({ ...editing, owner_id: v })} />
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => saveNode(editing)}>Save</button>
          </div>
        </Modal>
      )}

      {attachOpen && (
        <Modal isOpen title="Attach Billing Milestone" onClose={() => setAttachOpen(null)}>
          <FormField label="Milestone" type="select"
            options={billingMilestones
              .filter(m => !m.wbs_id || m.wbs_id === attachOpen.wbsId)
              .map(m => ({ value: m.id, label: `${m.contract_number} · ${m.description || 'Milestone'} · ${m.fixed_value || m.amount || ''}` }))}
            value={milestoneToAttach}
            onChange={v => setMilestoneToAttach(v)} />
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setAttachOpen(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={attach} disabled={!milestoneToAttach}>Attach</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * NetworkTab — flat activity list with predecessor selection.
 * Each activity hangs under a WBS node; predecessors are an array of
 * activity IDs (FS dependency by default — full type/lag stored in JSONB
 * on the backend but the UI keeps it simple for now).
 * ════════════════════════════════════════════════════════════════════════ */
function NetworkTab({ project, activities, wbs, users, onChange }) {
  const [editing, setEditing] = React.useState(null);
  const wbsLabel = (id) => (wbs.find(w => w.id === id)?.name) || '—';
  const actLabel = (id) => (activities.find(a => a.id === id)?.name) || id.slice(0, 8);

  async function save(act) {
    const url = act.id
      ? `/api/projects/${project.id}/activities/${act.id}`
      : `/api/projects/${project.id}/activities`;
    const method = act.id ? 'PUT' : 'POST';
    await apiFetch(url, { method, body: JSON.stringify({
      wbsId: act.wbs_id || null, name: act.name, description: act.description,
      plannedStart: act.planned_start || null, plannedEnd: act.planned_end || null,
      durationDays: act.duration_days, status: act.status,
      ownerId: act.owner_id || null, isMilestone: act.is_milestone || false,
      predecessors: (act.predecessors || []).map(p =>
        typeof p === 'string' ? { activity_id: p, type: 'FS', lag_days: 0 } : p),
    }) });
    setEditing(null); onChange();
  }
  async function del(id) {
    if (!confirm('Delete this activity?')) return;
    await apiFetch(`/api/projects/${project.id}/activities/${id}`, { method: 'DELETE' });
    onChange();
  }
  function predIds(p) {
    return (p || []).map(x => typeof x === 'string' ? x : x.activity_id).filter(Boolean);
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Activity Network</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setEditing({ status: 'pending', predecessors: [] })}>+ Add Activity</button>
      </div>
      <table className="data-table">
        <thead><tr><th>Activity</th><th>WBS</th><th>Start</th><th>End</th><th>Days</th><th>Predecessors</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {activities.map(a => (
            <tr key={a.id}>
              <td>{a.is_milestone ? '◆ ' : ''}<strong>{a.name}</strong></td>
              <td>{a.wbs_id ? wbsLabel(a.wbs_id) : '—'}</td>
              <td>{a.planned_start ? a.planned_start.substring(0,10) : '--'}</td>
              <td>{a.planned_end ? a.planned_end.substring(0,10) : '--'}</td>
              <td>{a.duration_days || '--'}</td>
              <td style={{ fontSize: 11 }}>{predIds(a.predecessors).map(actLabel).join(', ') || '--'}</td>
              <td><span className={`badge ${STATUS_BADGES[a.status] || 'badge-gray'}`}>{a.status}</span></td>
              <td>
                <button className="btn btn-secondary btn-xs" onClick={() => setEditing({ ...a, predecessors: predIds(a.predecessors) })}>Edit</button>
                <button className="btn btn-danger btn-xs" style={{ marginLeft: 4 }} onClick={() => del(a.id)}>Del</button>
              </td>
            </tr>
          ))}
          {activities.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94A3B8', padding: 30 }}>No activities yet.</td></tr>}
        </tbody>
      </table>

      {editing && (
        <Modal isOpen title={editing.id ? 'Edit Activity' : 'New Activity'} onClose={() => setEditing(null)}>
          <FormField label="Name" value={editing.name || ''} onChange={v => setEditing({ ...editing, name: v })} required />
          <FormField label="WBS Node" type="select"
            options={wbs.map(w => ({ value: w.id, label: `${w.wbs_code || ''} ${w.name}`.trim() }))}
            value={editing.wbs_id || ''} onChange={v => setEditing({ ...editing, wbs_id: v })} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <FormField label="Start" type="date" value={editing.planned_start ? editing.planned_start.substring(0,10) : ''} onChange={v => setEditing({ ...editing, planned_start: v })} />
            <FormField label="End" type="date" value={editing.planned_end ? editing.planned_end.substring(0,10) : ''} onChange={v => setEditing({ ...editing, planned_end: v })} />
            <FormField label="Duration (days)" type="number" value={editing.duration_days || ''} onChange={v => setEditing({ ...editing, duration_days: v })} />
          </div>
          <FormField label="Status" type="select" options={ACTIVITY_STATUSES.map(s => ({ value: s, label: s }))} value={editing.status || 'pending'} onChange={v => setEditing({ ...editing, status: v })} />
          <FormField label="Owner" type="employee-search" name="owner_id" options={users.map(u => ({ value: u.id, label: u.full_name }))} value={editing.owner_id || ''} onChange={(n, v) => setEditing({ ...editing, owner_id: v })} />
          {/* Predecessors as a multi-picker over the OTHER activities */}
          <MultiPicker
            label="Predecessors (Finish-to-Start)"
            options={activities.filter(a => a.id !== editing.id).map(a => ({ value: a.id, label: a.name }))}
            values={editing.predecessors || []}
            onChange={(arr) => setEditing({ ...editing, predecessors: arr })}
            placeholder="Add predecessor..." />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13 }}>
            <input type="checkbox" checked={!!editing.is_milestone} onChange={e => setEditing({ ...editing, is_milestone: e.target.checked })} />
            Milestone activity (zero duration)
          </label>
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => save(editing)}>Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * ProjectListRow — single row in the master pane. Two-line layout:
 *   line 1: project name (highlighted when selected) + small kind chip
 *   line 2: customer/contract subtitle + status pill
 * Edit/Delete actions appear inline; clicks on the row body select the
 * project, but the action buttons stop propagation so they don't do both.
 * ════════════════════════════════════════════════════════════════════════ */
function ProjectListRow({ project, selected, onClick, onEdit, onDelete }) {
  const STATUS_DOTS = {
    planning:  '#94A3B8', active: '#22C55E', on_hold: '#F59E0B',
    completed: '#3B82F6', cancelled: '#EF4444',
  };
  const KIND_COLORS = {
    'Internal':           '#A78BFA',
    'External-Dedicated': '#6FE6FF',
    'External-Shared':    '#34D399',
  };
  const kindColor = KIND_COLORS[project.project_kind] || '#94A3B8';
  return (
    <div onClick={onClick} style={{
      padding: '12px 14px', cursor: 'pointer',
      borderLeft: selected ? '3px solid #00E0FF' : '3px solid transparent',
      background: selected ? 'rgba(0,224,255,0.06)' : 'transparent',
      borderBottom: '1px solid rgba(255,255,255,0.04)',
      position: 'relative',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: selected ? '#6FE6FF' : '#E5E7EB',
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {project.name || '(untitled)'}
        </div>
        {project.project_kind && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, flexShrink: 0,
            color: kindColor, border: `1px solid ${kindColor}55`,
            background: `${kindColor}14`, textTransform: 'uppercase', letterSpacing: 0.4,
          }}>{project.project_kind.replace('External-', 'Ext-')}</span>
        )}
      </div>
      <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 2,
                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {project.customer_name || project.contract_number || project.project_code || '—'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#CBD5E1' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%',
                          background: STATUS_DOTS[project.status] || '#94A3B8' }} />
          {project.status || '—'}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={e => { e.stopPropagation(); onEdit(); }}
            title="Edit"
            style={{ padding: '2px 8px', fontSize: 10, borderRadius: 4,
                     border: '1px solid rgba(59,130,246,0.35)', color: '#3B82F6',
                     background: 'rgba(59,130,246,0.10)', cursor: 'pointer' }}>Edit</button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }}
            title="Delete"
            style={{ padding: '2px 8px', fontSize: 10, borderRadius: 4,
                     border: '1px solid rgba(244,63,94,0.35)', color: '#FB7185',
                     background: 'rgba(244,63,94,0.10)', cursor: 'pointer' }}>Del</button>
        </div>
      </div>
    </div>
  );
}
